import React from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import Menu from "./components/Menu";
import Register from "./pages/Register";
import Calendar from "./pages/Calendar";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import VisionRoyale from "./pages/VisionRoyale";
import History from "./pages/History";
import Projects from "./pages/Projects";
import Profile from "./pages/Profile";
import Contact from "./pages/Contact";
import Premium from "./pages/Premium";
import TermsOfUse from "./pages/TermsOfUse";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Matches from "./pages/Matches";
import MatchDetailsPage from "./pages/MatchDetailsPage";
import Admin from "./pages/Admin";
import SubscriptionPlans from "./pages/SubscriptionPlans";

import { BalanceProvider } from "./context/BalanceContext";
import { ProjectsProvider } from "./context/ProjectsContext";
import { UserProvider, useUser } from "./context/UserContext";
import { LanguageProvider } from "./context/LanguageContext";
import AdminLayout from "./admin/AdminLayout";
import Users from "./admin/Users";
import Scripts from "./admin/Scripts";
import Dashboard from "./admin/Dashboard";
import Algorithm from "./admin/Algorithm";
import Tasks from "./admin/Tasks";
import DbMgr from "./admin/DbMgr";
import PredictionSettings from "./admin/PredictionSettings";
import Scans from "./admin/Scans";
import Backtest from "./admin/Backtest";
import BacktestValue from "./admin/BacktestValue";
import Tickets from "./pages/Tickets";
import ProjectDetails from "./pages/ProjectDetails";
import TicketDetails from "./pages/TicketDetails";
import NewTicket from "./pages/NewTicket";

const ProtectedRouteWrapper = ({ children }) => {
  const { user } = useUser();
  return user && user !== "Guest" ? children : <Navigate to="/login" replace />;
};

function AdminRoute({ children }) {
  const { user } = useUser();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

const HomeRoute = () => {
  const { user } = useUser();

  if (user && user !== "Guest") {
    return <Calendar />;
  }

  return <VisionRoyale />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Menu />,
    children: [
      {
        index: true,
        element: <HomeRoute />,
      },
      {
        path: "calendar",
        element: (
          <ProtectedRouteWrapper>
            <Calendar />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "match/:id",
        element: (
          <ProtectedRouteWrapper>
            <MatchDetailsPage />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "register",
        element: <Register />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "logout",
        element: <Logout />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password/:token",
        element: <ResetPassword />,
      },
      {
        path: "history",
        element: (
          <ProtectedRouteWrapper>
            <History />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "projects",
        element: (
          <ProtectedRouteWrapper>
            <Projects />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "projects/:id",
        element: (
          <ProtectedRouteWrapper>
            <ProjectDetails />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "tickets",
        element: (
          <ProtectedRouteWrapper>
            <Tickets />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "tickets/new",
        element: (
          <ProtectedRouteWrapper>
            <NewTicket />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "tickets/:id",
        element: (
          <ProtectedRouteWrapper>
            <TicketDetails />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "profile",
        element: (
          <ProtectedRouteWrapper>
            <Profile />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "contact",
        element: (
          <ProtectedRouteWrapper>
            <Contact />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "terms-of-use",
        element: <TermsOfUse />,
      },
      {
        path: "privacy",
        element: <Privacy />,
      },
      {
        path: "matches",
        element: (
          <ProtectedRouteWrapper>
            <Matches />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "subscription-plans",
        element: (
          <ProtectedRouteWrapper>
            <SubscriptionPlans />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "premium",
        element: (
          <ProtectedRouteWrapper>
            <Premium />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "admin",
        element: (
          <ProtectedRouteWrapper>
            <Admin />
          </ProtectedRouteWrapper>
        ),
      },
      {
        path: "xadmin",
        element: (
          <ProtectedRouteWrapper>
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          </ProtectedRouteWrapper>
        ),
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: "users",
            element: <Users />,
          },
          {
            path: "scripts",
            element: <Scripts />,
          },
          {
            path: "algorithm",
            element: <Algorithm />,
          },
          {
            path: "tasks",
            element: <Tasks />,
          },
          {
            path: "dbmgr",
            element: <DbMgr />,
          },
          {
            path: "psettings",
            element: <PredictionSettings />,
          },
          {
            path: "scans",
            element: <Scans />,
          },
          {
            path: "backtest",
            element: <Backtest />
          },
          {
            path: "vbacktest",
            element: <BacktestValue />
          }
        ]
      }
    ],
  },
]);

const App = () => {
  return (
    <LanguageProvider>
      <UserProvider>
        <BalanceProvider>
          <ProjectsProvider>
            <RouterProvider router={router} />
          </ProjectsProvider>
        </BalanceProvider>
      </UserProvider>
    </LanguageProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

reportWebVitals();
